import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useFinanceCalculator } from '../FinanceCalculator/FinanceCalculator.hook';
import { useCarFinanceLoan } from '../../queries/hooks/useCarFinanceLoan';
import { useTotalDepositContext } from '../../contexts/TotalDepositContext';

import { Text } from '../../../../components/ToolkitV2/Text/Text';
import { LinkButton } from '../../../Toolkit/Button/LinkButton';
import { Link } from '../../../Toolkit/Button/Link';
import { SkeletonBlock } from '../../../ToolkitV2/Skeleton/Skeleton';
import { TextWithHelpIcon } from '../Panels/shared/TextWithHelpIcon/TextWithHelpIcon';
import { Spacer } from '../../../../helpers/genericStyles';
import { PriceField } from '../../../../components/CarFinancePage/financeProviders/shared/PriceField/PriceField';
import { Select } from '../../../../components/Toolkit/Inputs/Select';
import { AdCard } from '../Panels/Introduction/components/AdCard/AdCard';
import { Divider } from '../../../ToolkitV2/Divider/Divider';

import * as Styled from './FinanceCalculatorV2.styled';

import type { Form, LoanRequest } from '../../queries/schema/carFinance.schema';
import { FinanceCalculatorV2Props } from './FinanceCalculatorV2.typed';
import {
  formatCurrencyDecimal,
  formatCurrencyInteger,
  formatPercent,
  DynamicCashDepositModal,
  DynamicTradeInValueModal,
  DynamicTotalDepositModal,
  DynamicOutstandingFinanceModal,
} from './helpers';

const BreakdownValue = ({
  isLoading,
  value,
  format,
  skeletonWidth = '64px',
  skeletonHeight = '14px',
}: {
  readonly isLoading: boolean;
  readonly value: number | null | undefined;
  readonly format: (v: number) => string;
  readonly skeletonWidth?: string;
  readonly skeletonHeight?: string;
}) => {
  if (isLoading)
    return <SkeletonBlock as="span" w={skeletonWidth} h={skeletonHeight} />;
  if (value == null) return <>{'—'}</>;
  return <>{format(value)}</>;
};

const FinanceCalculatorV2 = (props: FinanceCalculatorV2Props) => {
  const {
    dealerId,
    financeHouse,
    minimumDepositValue,
    minimumDepositPercentage,
    showOutstandingFinance = false,
    adId,
    onViewFullSummary,
    onFinanceTypeHelp,
    onChangePayload,
  } = props;

  const onChangePayloadRef = useRef(onChangePayload);
  onChangePayloadRef.current = onChangePayload;

  const {
    price,
    control,
    getValues,
    onChangeHandlerFinanceInputs,
    tradeInValue,
    outstandingFinance,
    cashDeposit,
    onBlurFinanceInputs,
    isCashDepositModalOpen,
    setIsCashDepositModalOpen,
    isTradeInValueModalOpen,
    setIsTradeInValueModalOpen,
    isTotalDepositModalOpen,
    setIsTotalDepositModalOpen,
    isOutstandingFinanceModalOpen,
    setIsOutstandingFinanceModalOpen,
    isMonthlyRepaymentsDisabled,
  } = useFinanceCalculator({
    minimumDepositValue,
    minimumDepositPercentage,
  });

  // Future: remove this once the calculator owns its form via useForm<CalculatorFormValues>().
  // At that point setValue will come from the calculator's own useForm hook.
  const { setValue } = useFormContext<Form>();

  // Future: useWatch will be typed against CalculatorFormValues rather than the
  // full Form union once the calculator owns its form state.
  const [make, model, totalDeposit, financeAmount, monthlyInstallments] =
    useWatch({
      control,
      name: [
        'carDetails.make',
        'carDetails.model',
        'loanDetails.totalDeposit',
        'loanDetails.financeAmount',
        'loanDetails.monthlyInstallments',
      ],
    });

  // Future: replace with an internal debounce + validation when the calculator
  // no longer relies on TotalDepositProvider from the parent form.
  const { debouncedTotalDeposit, isValidDeposit } = useTotalDepositContext();

  const loanDetails = getValues('loanDetails');

  const loanOptions: LoanRequest = {
    ...loanDetails,
    monthlyInstallmentOptions: [],
    dealerId,
    financeHouse,
  };

  const { data, isLoading } = useCarFinanceLoan({
    options: loanOptions,
    enabled: isValidDeposit,
    dependencies: [debouncedTotalDeposit, monthlyInstallments],
  });

  useEffect(() => {
    const loanResult = isValidDeposit ? data : null;
    const pricePerMonth = loanResult ? loanResult.pricePerMonth : 0;
    const apr = loanResult ? loanResult.apr : undefined;
    const totalCostOfCredit = loanResult ? loanResult.costOfCredit : undefined;

    // Future: remove once the calculator owns its form state.
    setValue('loanDetails.pricePerMonth', pricePerMonth);

    // Future: this is the primary integration point for the IIFE host.
    // The host passes onChangePayload to receive updated loan values and
    // can use them to update external UI without any React or form context.
    onChangePayloadRef.current?.({
      monthlyPrice: pricePerMonth || undefined,
      financeType: 'Hire Purchase',
      totalDeposit,
      financeAmount,
      apr,
      totalCostOfCredit,
    });
  }, [data, isValidDeposit, setValue, totalDeposit, financeAmount]);

  const loanData = isValidDeposit && data ? data : null;

  const monthlyPrice = loanData?.pricePerMonth;
  const apr = loanData?.apr;
  const totalCostOfCredit = loanData?.costOfCredit;

  return (
    <>
      <Styled.Heading forwardedAs="h3" size="L18" weight="bold">
        Calculate monthly repayments
      </Styled.Heading>
      <Spacer marginBottom={'M24'} />
      <Styled.Grid>
        <div>
          {adId && price != null && (
            <AdCard
              adId={adId}
              title={`${make ?? ''} ${model ?? ''}`}
              price={price}
            />
          )}
          <Spacer marginBottom={'M24'} />
          <Styled.InputsContainer>
            <Controller
              name="loanDetails.cashDeposit"
              control={control}
              render={({ field, fieldState }) => (
                <PriceField
                  {...field}
                  {...fieldState}
                  onChange={(value: number | undefined) => {
                    field.onChange(value);
                    onChangeHandlerFinanceInputs({
                      cashDeposit: value,
                      tradeInValue,
                      outstandingFinance,
                      fieldName: field.name,
                    });
                  }}
                  onBlur={onBlurFinanceInputs(field.name)}
                  willUseSubText={false}
                  label={
                    <TextWithHelpIcon
                      text="Cash deposit*"
                      onClickIcon={() => setIsCashDepositModalOpen(true)}
                      aria-label="Cash deposit modal button"
                    />
                  }
                />
              )}
            />
            <Controller
              name="loanDetails.tradeInValue"
              control={control}
              render={({ field, fieldState }) => (
                <PriceField
                  {...field}
                  {...fieldState}
                  onChange={(value: number | undefined) => {
                    field.onChange(value);
                    onChangeHandlerFinanceInputs({
                      cashDeposit,
                      tradeInValue: value,
                      outstandingFinance,
                      fieldName: field.name,
                    });
                  }}
                  onBlur={onBlurFinanceInputs(field.name)}
                  willUseSubText={false}
                  label={
                    <TextWithHelpIcon
                      text="Estimated trade-in value"
                      onClickIcon={() => setIsTradeInValueModalOpen(true)}
                      aria-label="Trade-in value modal button"
                    />
                  }
                  topRightNode={
                    <Link
                      href="https://www.donedeal.ie/value-my-trade-in"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Get estimate
                    </Link>
                  }
                />
              )}
            />
            {showOutstandingFinance && (
              <Controller
                name="loanDetails.outstandingLoan"
                control={control}
                render={({ field, fieldState }) => (
                  <PriceField
                    {...field}
                    {...fieldState}
                    onChange={(value: number | undefined) => {
                      field.onChange(value);
                      onChangeHandlerFinanceInputs({
                        cashDeposit,
                        tradeInValue,
                        outstandingFinance: value,
                        fieldName: field.name,
                      });
                    }}
                    onBlur={onBlurFinanceInputs(field.name)}
                    willUseSubText={false}
                    label={
                      <TextWithHelpIcon
                        text="Outstanding Finance"
                        onClickIcon={() =>
                          setIsOutstandingFinanceModalOpen(true)
                        }
                        aria-label="Outstanding finance modal button"
                      />
                    }
                  />
                )}
              />
            )}
          </Styled.InputsContainer>
        </div>
        <Styled.BreakdownPanel>
          <Styled.MonthlyPriceHeader>
            <div>
              <Text size="M14" color="GREY_DARK">
                Monthly price
              </Text>
              <Text size="H32" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={monthlyPrice}
                  format={formatCurrencyDecimal}
                  skeletonWidth="100px"
                  skeletonHeight="28px"
                />
              </Text>
            </div>
            <Controller
              name="loanDetails.monthlyInstallments"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <Select
                  {...field}
                  value={value?.toString()}
                  onChange={(val) => onChange(Number(val))}
                  options={
                    getValues('loanDetails.monthlyInstallmentOptions')?.map(
                      (opt) => ({
                        label: opt.displayValue,
                        value: opt.value.toString(),
                      }),
                    ) ?? []
                  }
                  label="Select a Term*"
                  disabled={isMonthlyRepaymentsDisabled}
                />
              )}
            />
          </Styled.MonthlyPriceHeader>
          <Divider />
          <Spacer marginBottom={'M24'} />
          <Styled.BreakdownList>
            <Styled.BreakdownItem>
              <Text size="M14" color="GREY_DARK">
                Finance Type
              </Text>
              {onFinanceTypeHelp ? (
                <TextWithHelpIcon
                  text="Hire Purchase"
                  onClickIcon={onFinanceTypeHelp}
                  size="M14"
                  color="GREY_DARKER"
                  weight="bold"
                  aria-label="Finance type help"
                />
              ) : (
                <Text size="M14" weight="bold" color="GREY_DARKER">
                  Hire Purchase
                </Text>
              )}
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M14" color="GREY_DARK">
                Total Deposit
              </Text>
              <Text size="M14" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={totalDeposit}
                  format={formatCurrencyInteger}
                />
              </Text>
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M14" color="GREY_DARK">
                Finance Amount
              </Text>
              <Text size="M14" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={financeAmount}
                  format={formatCurrencyInteger}
                />
              </Text>
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M14" color="GREY_DARK">
                Typical APR
              </Text>
              <Text size="M14" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={apr}
                  format={formatPercent}
                  skeletonWidth="48px"
                />
              </Text>
            </Styled.BreakdownItem>
            <Styled.BreakdownItem>
              <Text size="M14" color="GREY_DARK">
                Total cost of credit
              </Text>
              <Text size="M14" weight="bold" color="GREY_DARKER">
                <BreakdownValue
                  isLoading={isLoading}
                  value={totalCostOfCredit}
                  format={formatCurrencyInteger}
                  skeletonWidth="48px"
                />
              </Text>
            </Styled.BreakdownItem>
          </Styled.BreakdownList>
          <>
            <br />
            <LinkButton
              onClick={onViewFullSummary}
              colorVariant="BLACK"
              underline
            >
              View full summary
            </LinkButton>
          </>
        </Styled.BreakdownPanel>
        <DynamicCashDepositModal
          isOpen={isCashDepositModalOpen}
          onRequestClose={() => setIsCashDepositModalOpen(false)}
        />
        <DynamicTradeInValueModal
          isOpen={isTradeInValueModalOpen}
          onRequestClose={() => setIsTradeInValueModalOpen(false)}
        />
        <DynamicTotalDepositModal
          isOpen={isTotalDepositModalOpen}
          onRequestClose={() => setIsTotalDepositModalOpen(false)}
        />
        <DynamicOutstandingFinanceModal
          isOpen={isOutstandingFinanceModalOpen}
          onRequestClose={() => setIsOutstandingFinanceModalOpen(false)}
        />
      </Styled.Grid>
      <Spacer marginBottom={'L40'} />
      <Divider />
    </>
  );
};

export { FinanceCalculatorV2 };
